import { useState, useEffect, useCallback } from "react";
import { api, uploadPhoto, API_BASE } from "../../api";
import { CONTAINER_TYPES } from "../../constants";
import { useInterviewState } from "./useInterviewState";
import PhotoCapture from "./PhotoCapture";
import ItemReview from "./ItemReview";

function StepIndicator({ currentStep, totalSteps }) {
  return (
    <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 24 }}>
      {Array.from({ length: totalSteps }, (_, i) => (
        <div
          key={i}
          style={{
            width: i + 1 === currentStep ? 24 : 8,
            height: 8,
            borderRadius: 4,
            background: i + 1 <= currentStep ? "#F0C75E" : "#333",
            transition: "all 0.3s ease",
          }}
        />
      ))}
    </div>
  );
}

function LoadingSpinner({ message }) {
  return (
    <div style={{ textAlign: "center", padding: 40 }}>
      <div
        style={{
          width: 48,
          height: 48,
          border: "4px solid #333",
          borderTopColor: "#F0C75E",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto 16px",
        }}
      />
      <div style={{ color: "#aaa", fontSize: 14 }}>{message || "Processing..."}</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function InterviewFlow({ onComplete, onCancel }) {
  const {
    state,
    setContainerType,
    setPhoto,
    setContainerName,
    setItems,
    updateItem,
    removeItem,
    addItem,
    setLocationId,
    setStep,
    nextStep,
    prevStep,
    setAnalyzing,
    setError,
    reset,
    clearDraft,
  } = useInterviewState();

  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedResult, setSavedResult] = useState(null);

  // Fetch locations
  useEffect(() => {
    api("GET", "/locations")
      .then((data) => setLocations(data.locations || []))
      .catch(console.error);
  }, []);

  // Handle photo analysis
  const analyzePhoto = useCallback(async () => {
    if (!state.photoBlob) {
      setError("No photo to analyze");
      return;
    }

    setStep(3); // Analyzing step
    setAnalyzing(true);

    try {
      // First upload the photo
      const uploadResult = await uploadPhoto(state.photoBlob, "container", "pending");
      const photoUrl = uploadResult.url;

      // Then analyze with Claude Vision
      const analysisResult = await api("POST", "/interview/analyze", {
        photo_url: photoUrl,
        container_type: state.containerType,
      });

      // Update state with results
      setPhoto(photoUrl, null);
      setContainerName(analysisResult.container_name_suggestion || "");
      setItems(
        (analysisResult.items || []).map((item) => ({
          name: item.name,
          quantity: item.quantity || 1,
          categories: item.category_suggestion ? [item.category_suggestion] : [],
          is_consumable: item.is_consumable || false,
        }))
      );

      setStep(4); // Review items step
    } catch (err) {
      console.error("Analysis error:", err);
      setError(err.message || "Failed to analyze photo");
      setStep(2); // Back to photo step
    } finally {
      setAnalyzing(false);
    }
  }, [state.photoBlob, state.containerType, setStep, setAnalyzing, setPhoto, setContainerName, setItems, setError]);

  // Handle save
  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);

    try {
      const result = await api("POST", "/interview/save", {
        container: {
          name: state.containerName,
          type: state.containerType,
          location_id: state.locationId,
          photo_url: state.photoUrl,
        },
        items: state.items,
      });

      setSavedResult(result);
      clearDraft();
      setStep(6); // Success step
    } catch (err) {
      console.error("Save error:", err);
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [state, setStep, setError, clearDraft]);

  // Step 1: Container Type
  if (state.step === 1) {
    return (
      <div style={{ padding: 20 }}>
        <StepIndicator currentStep={1} totalSteps={5} />

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <h2 style={{ margin: 0, color: "#F0F0F0", fontSize: 20, fontWeight: 700 }}>
            What type of container?
          </h2>
          <p style={{ color: "#888", fontSize: 13, marginTop: 8 }}>
            This helps Claude better identify items inside
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {CONTAINER_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => setContainerType(type.id)}
              style={{
                padding: "20px 16px",
                borderRadius: 14,
                border: `2px solid ${state.containerType === type.id ? "#F0C75E" : "#252545"}`,
                background: state.containerType === type.id ? "#F0C75E15" : "#16162a",
                cursor: "pointer",
                textAlign: "center",
                transition: "all 0.2s ease",
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>{type.icon}</div>
              <div style={{ color: state.containerType === type.id ? "#F0F0F0" : "#888", fontSize: 13, fontWeight: 600 }}>
                {type.label}
              </div>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: 10,
              border: "1px solid #333",
              background: "transparent",
              color: "#888",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Cancel
          </button>
          <button
            onClick={nextStep}
            style={{
              flex: 2,
              padding: "14px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #F0C75E, #E8927C)",
              color: "#000",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Next: Take Photo
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Photo Capture
  if (state.step === 2) {
    return (
      <div style={{ padding: 20 }}>
        <StepIndicator currentStep={2} totalSteps={5} />

        {state.error && (
          <div
            style={{
              padding: "12px 16px",
              background: "#E8927C22",
              border: "1px solid #E8927C44",
              borderRadius: 10,
              color: "#E8927C",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {state.error}
          </div>
        )}

        <PhotoCapture
          onPhotoCapture={(url, blob) => {
            setPhoto(url, blob);
          }}
          onSkip={() => {
            // Skip photo, go to manual item entry
            setStep(4);
          }}
        />

        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <button
            onClick={prevStep}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: 10,
              border: "1px solid #333",
              background: "transparent",
              color: "#888",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Back
          </button>
          {state.photoUrl && (
            <button
              onClick={analyzePhoto}
              style={{
                flex: 2,
                padding: "14px",
                borderRadius: 10,
                border: "none",
                background: "linear-gradient(135deg, #F0C75E, #E8927C)",
                color: "#000",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Analyze with Claude
            </button>
          )}
        </div>
      </div>
    );
  }

  // Step 3: Analyzing
  if (state.step === 3) {
    return (
      <div style={{ padding: 20 }}>
        <StepIndicator currentStep={3} totalSteps={5} />
        <LoadingSpinner message="Claude is identifying items..." />
        <p style={{ textAlign: "center", color: "#666", fontSize: 12, marginTop: 16 }}>
          This usually takes 5-10 seconds
        </p>
      </div>
    );
  }

  // Step 4: Review Items
  if (state.step === 4) {
    return (
      <div style={{ padding: 20 }}>
        <StepIndicator currentStep={4} totalSteps={5} />

        {state.error && (
          <div
            style={{
              padding: "12px 16px",
              background: "#E8927C22",
              border: "1px solid #E8927C44",
              borderRadius: 10,
              color: "#E8927C",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {state.error}
          </div>
        )}

        <ItemReview
          items={state.items}
          containerName={state.containerName}
          onUpdateItem={updateItem}
          onRemoveItem={removeItem}
          onAddItem={addItem}
          onUpdateContainerName={setContainerName}
        />

        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <button
            onClick={() => setStep(2)}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: 10,
              border: "1px solid #333",
              background: "transparent",
              color: "#888",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Retake Photo
          </button>
          <button
            onClick={nextStep}
            disabled={!state.containerName || state.items.length === 0}
            style={{
              flex: 2,
              padding: "14px",
              borderRadius: 10,
              border: "none",
              background: !state.containerName || state.items.length === 0
                ? "#333"
                : "linear-gradient(135deg, #F0C75E, #E8927C)",
              color: !state.containerName || state.items.length === 0 ? "#666" : "#000",
              fontSize: 14,
              fontWeight: 700,
              cursor: !state.containerName || state.items.length === 0 ? "not-allowed" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Next: Location
          </button>
        </div>
      </div>
    );
  }

  // Step 5: Location Selection
  if (state.step === 5) {
    return (
      <div style={{ padding: 20 }}>
        <StepIndicator currentStep={5} totalSteps={5} />

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <h2 style={{ margin: 0, color: "#F0F0F0", fontSize: 20, fontWeight: 700 }}>
            Where is this container?
          </h2>
          <p style={{ color: "#888", fontSize: 13, marginTop: 8 }}>
            Select a location or skip for now
          </p>
        </div>

        {state.error && (
          <div
            style={{
              padding: "12px 16px",
              background: "#E8927C22",
              border: "1px solid #E8927C44",
              borderRadius: 10,
              color: "#E8927C",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {state.error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {locations.length === 0 ? (
            <div style={{ textAlign: "center", color: "#666", padding: 20 }}>
              No locations found. You can add them later.
            </div>
          ) : (
            locations.map((loc) => (
              <button
                key={loc.id}
                onClick={() => setLocationId(loc.id)}
                style={{
                  padding: "14px 16px",
                  borderRadius: 10,
                  border: `2px solid ${state.locationId === loc.id ? "#F0C75E" : "#252545"}`,
                  background: state.locationId === loc.id ? "#F0C75E15" : "#16162a",
                  color: state.locationId === loc.id ? "#F0F0F0" : "#aaa",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {"\u{1F4CD}"} {loc.name}
              </button>
            ))
          )}
        </div>

        {/* Summary */}
        <div
          style={{
            padding: 16,
            background: "#1a1a3e",
            borderRadius: 12,
            marginBottom: 20,
          }}
        >
          <div style={{ color: "#888", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>Summary</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {state.photoUrl && (
              <img
                src={state.photoUrl.startsWith("blob:") ? state.photoUrl : `${API_BASE.replace("/api", "")}${state.photoUrl}`}
                alt="Container"
                style={{ width: 60, height: 60, borderRadius: 8, objectFit: "cover" }}
              />
            )}
            <div>
              <div style={{ color: "#F0F0F0", fontSize: 15, fontWeight: 600 }}>{state.containerName}</div>
              <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>
                {state.items.length} item{state.items.length !== 1 ? "s" : ""} • {CONTAINER_TYPES.find(t => t.id === state.containerType)?.label}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={prevStep}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: 10,
              border: "1px solid #333",
              background: "transparent",
              color: "#888",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Back
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 2,
              padding: "14px",
              borderRadius: 10,
              border: "none",
              background: saving ? "#333" : "linear-gradient(135deg, #7ECEC1, #6B9BD2)",
              color: saving ? "#666" : "#000",
              fontSize: 14,
              fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {saving ? "Saving..." : "Save Container"}
          </button>
        </div>
      </div>
    );
  }

  // Step 6: Success
  if (state.step === 6 && savedResult) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>{"\u2705"}</div>
        <h2 style={{ margin: 0, color: "#F0F0F0", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
          Container Added!
        </h2>
        <p style={{ color: "#888", fontSize: 14, marginBottom: 24 }}>
          {savedResult.container.name} with {savedResult.items_created} items
        </p>

        <div
          style={{
            padding: 20,
            background: "#16162a",
            borderRadius: 14,
            marginBottom: 24,
          }}
        >
          <div style={{ color: "#888", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>QR Code</div>
          <div
            style={{
              width: 80,
              height: 80,
              background: "#fff",
              borderRadius: 8,
              margin: "0 auto 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid #333",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
              {[1, 1, 0, 1, 0, 1, 0, 1, 1].map((v, i) => (
                <div key={i} style={{ width: 12, height: 12, background: v ? "#000" : "#fff" }} />
              ))}
            </div>
          </div>
          <div style={{ color: "#F0C75E", fontSize: 14, fontWeight: 700, fontFamily: "monospace" }}>
            {savedResult.container.qr_code}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={() => {
              reset();
            }}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: 10,
              border: "1px solid #333",
              background: "#1a1a3e",
              color: "#aaa",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Add Another
          </button>
          <button
            onClick={() => {
              reset();
              onComplete?.(savedResult);
            }}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #F0C75E, #E8927C)",
              color: "#000",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return null;
}
